import { getChannelLabel } from '@/lib/format-utils'
import { REVIEWS_ICON } from '@/lib/nav-icons'
import {
  closesInWords,
  reviewRowHref,
  type ReviewToLeave,
  type ReviewsToLeave,
} from '@/lib/review-events'
import { formatSupplyDay } from '@/lib/supply-events'
import { BLOCK_CLASSES } from '@/components/calendar/event-block-meta'
import type { DayListGroup, DayListRow } from './day-list'

/**
 * The "Reviews to leave" list as day-list rows: the same row, the same sheet,
 * the same `open=stay:<id>&section=reviews` target as a review block on the grid.
 *
 * Every word about time comes from the API's days (`Closes in 3 days` is the
 * closing day against the read's own `generated_at` day). A group that is
 * "unknown" says so; it is never counted or ticked as done.
 */

const TILE: Record<ReviewToLeave['kind'], string> = {
  owed: BLOCK_CLASSES.attention.outlined,
  thank_you: BLOCK_CLASSES.routine.filled,
  untracked: 'border-border bg-transparent text-textMuted',
}

function rowFor(
  item: ReviewToLeave,
  {
    today,
    guestNames,
    onOpen,
  }: {
    today: string | null
    guestNames: ReadonlyMap<string, string | null>
    onOpen: (reservationId: string) => void
  }
): DayListRow {
  const { review, kind } = item
  const id = review.reservation_id
  const checkedOut = review.check_out ? formatSupplyDay(review.check_out) : null
  // The month's stays name the guest; a stay that checked out in the month before
  // is not in them (the layer carries no name), so it is told by its checkout day.
  const named = guestNames.get(id)
  const guest = named || (checkedOut ? `Guest checked out ${checkedOut}` : `Reservation ${id}`)
  const closes = closesInWords(review.window_closes_on, today)
  const closesOn = review.window_closes_on ? formatSupplyDay(review.window_closes_on) : null
  const facts = [
    closes && closesOn ? `${closes} (${closesOn})` : closes,
    kind === 'thank_you' && review.guest_reviewed_on
      ? `Guest reviewed ${formatSupplyDay(review.guest_reviewed_on)}`
      : null,
    item.thankYouPending ? 'Thank-you not sent' : null,
    review.channel ? getChannelLabel(review.channel) : null,
    named && checkedOut ? `Checked out ${checkedOut}` : null,
  ].filter(Boolean)
  const detail = facts.join(' · ')
  const what =
    kind === 'owed'
      ? 'Your review is due'
      : kind === 'thank_you'
        ? 'Thank-you not sent'
        : 'Not tracked'
  return {
    key: `reviews-to-leave:${kind}:${id}`,
    tileClassName: TILE[kind],
    icon: <REVIEWS_ICON className="h-4 w-4" aria-hidden="true" />,
    title: guest,
    detail,
    ariaLabel: `${what}: ${guest}, ${detail} - open review details`,
    testId: 'reviews-to-leave-row',
    data: { 'data-reservation-id': id, 'data-review-to-leave': kind },
    onOpen: () => onOpen(id),
    // A stay with no chase record has no draft on the Reviews page to go to.
    link:
      kind === 'untracked'
        ? undefined
        : {
            label: 'Open drafts on the Reviews page',
            to: reviewRowHref(id),
            testId: 'reviews-to-leave-drafts-link',
          },
  }
}

/** The three groups that have rows, owed first. */
export function reviewsToLeaveGroups(
  list: ReviewsToLeave,
  context: {
    today: string | null
    guestNames: ReadonlyMap<string, string | null>
    onOpen: (reservationId: string) => void
  }
): DayListGroup[] {
  const rows = (items: ReviewToLeave[]) => items.map(item => rowFor(item, context))
  const groups: DayListGroup[] = [
    { id: 'reviews-owed', heading: 'Reviews to leave', rows: rows(list.owed) },
    {
      id: 'reviews-thank-you',
      heading: 'Thank-you not sent',
      note: 'The guest has reviewed. Your thank-you is drafted on the Reviews page.',
      rows: rows(list.thankYou),
    },
    {
      id: 'reviews-untracked',
      heading: 'Not tracked',
      note: 'No review chase is on record for these stays, so whether you have reviewed is not known here. They are not counted.',
      rows: rows(list.untracked),
    },
  ]
  return groups.filter(group => group.rows.length > 0)
}
